import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowRight, Users, TrendingUp, Mail, BarChart3, CheckCircle, Zap, Calendar, MessageSquare, Workflow, Bell } from 'lucide-react';
import AuthModal from '../components/AuthModal';
import { useI18n } from '../contexts/I18nContext';

const Landing = () => {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('login');
  const { language, setLanguage, t } = useI18n();

  // Use useEffect to handle navigation after user state changes
  useEffect(() => {
    if (user && !loading) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  // Show loading state while authentication is being processed
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0176D3] mx-auto"></div>
          <p className="mt-2 text-gray-600">{t('auth.authenticating', 'Authenticating...')}</p>
        </div>
      </div>
    );
  }

  const handleOpenAuthModal = (mode = 'login') => {
    setAuthModalMode(mode);
    setIsAuthModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-bissa-blue to-bissa-blue-700 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">B</span>
              </div>
              <h1 className="text-2xl font-bold text-bissa-blue">Bissa</h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden md:flex items-center gap-4 text-sm text-gray-600">
                <a href="/privacy" className="hover:text-bissa-blue transition-colors">
                  {t('landing.privacy', 'Privacy')}
                </a>
                <a href="/terms" className="hover:text-bissa-blue transition-colors">
                  {t('landing.terms', 'Terms')}
                </a>
              </div>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bissa-blue"
              >
                <option value="en">EN</option>
                <option value="es">ES</option>
                <option value="fr">FR</option>
                <option value="sw">SW</option>
                <option value="ar">AR</option>
                <option value="pt">PT</option>
                <option value="ha">HA</option>
                <option value="yo">YO</option>
                <option value="am">AM</option>
                <option value="ig">IG</option>
                <option value="auto">AUTO</option>
              </select>
              <button
                onClick={() => handleOpenAuthModal('login')}
                className="px-6 py-2.5 bg-bissa-blue text-white rounded-lg hover:bg-bissa-gold hover:text-bissa-blue transition-all font-medium shadow-sm hover:shadow-md"
              >
                {t('landing.signIn', 'Sign In')}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-20 sm:pt-32 pb-16 sm:pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="text-center lg:text-left order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-blue-50 rounded-full text-xs sm:text-sm font-medium text-bissa-blue mb-4 sm:mb-6">
                <Zap className="w-3 h-3 sm:w-4 sm:h-4" />
                {t('landing.heroBadge','Where Heritage Meets AI')}
              </div>
              
              <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-bissa-blue mb-4 sm:mb-6 leading-tight">
                {t('landing.heroTitlePrefix','Build Relationships')}
                <br />
                <span className="text-bissa-gold">{t('landing.heroTitleHighlight','Like the Bissa')}</span>
              </h2>
              
              <p className="text-base sm:text-lg lg:text-xl text-gray-700 mb-6 sm:mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                {t('landing.heroDescription', 'Just as the Bissa people of Burkina Faso built thriving communities through trust and connection, Bissa.ai empowers your business with AI-driven insights that strengthen every relationship.')}
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8 justify-center lg:justify-start">
                <button
                  onClick={() => handleOpenAuthModal('register')}
                  className="px-6 sm:px-8 py-3 sm:py-4 bg-bissa-blue text-white rounded-lg hover:bg-bissa-blue-700 transition-all font-semibold text-base sm:text-lg inline-flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
                >
                  {t('landing.ctaPrimary','Get Started Free')}
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                <button
                  onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                  className="px-6 sm:px-8 py-3 sm:py-4 bg-white text-bissa-blue rounded-lg hover:bg-gray-50 transition-all font-semibold text-base sm:text-lg border-2 border-bissa-blue"
                >
                  {t('landing.ctaSecondary','See Features')}
                </button>
              </div>

              <div className="flex items-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>{t('landing.badgeNoCard','No credit card required')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>{t('landing.badgeFree','Free forever')}</span>
                </div>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative lg:pl-4 order-1 lg:order-2">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/images/bissa-hero-collaboration.jpg"
                  alt="African professionals collaborating on business"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h3 className="text-3xl sm:text-4xl font-bold text-bissa-blue mb-4">
              {t('landing.featuresTitle','Powerful Features for African Businesses')}
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('landing.featuresDescription','Everything you need to build lasting business relationships across Africa and beyond.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-bissa-blue/10 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-bissa-blue" />
              </div>
              <h4 className="text-xl font-semibold text-bissa-blue mb-3">
                {t('landing.feature1Title','Smart Contact Management')}
              </h4>
              <p className="text-gray-600">
                {t('landing.feature1Description','Organize and track all your business contacts with AI-powered insights and relationship scoring.')}
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-bissa-blue/10 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-bissa-blue" />
              </div>
              <h4 className="text-xl font-semibold text-bissa-blue mb-3">
                {t('landing.feature2Title','Sales Pipeline Tracking')}
              </h4>
              <p className="text-gray-600">
                {t('landing.feature2Description','Visualize your sales process and track deals from lead to close with customizable stages.')}
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-bissa-blue/10 rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-bissa-blue" />
              </div>
              <h4 className="text-xl font-semibold text-bissa-blue mb-3">
                {t('landing.feature5Title','AI-Powered Insights')}
              </h4>
              <p className="text-gray-600">
                {t('landing.feature5Description','Leverage artificial intelligence to get actionable insights about your business relationships.')}
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-bissa-blue/10 rounded-lg flex items-center justify-center mb-4">
                <Workflow className="w-6 h-6 text-bissa-blue" />
              </div>
              <h4 className="text-xl font-semibold text-bissa-blue mb-3">
                Advanced Automations
              </h4>
              <p className="text-gray-600">
                Automate workflows with 30+ triggers and 40+ actions. Connect Slack, send SMS, create tasks, and more with pre-built templates.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-bissa-blue/10 rounded-lg flex items-center justify-center mb-4">
                <Calendar className="w-6 h-6 text-bissa-blue" />
              </div>
              <h4 className="text-xl font-semibold text-bissa-blue mb-3">
                Calendar Integration
              </h4>
              <p className="text-gray-600">
                Sync with Google Calendar and Outlook. Schedule meetings, track events, and never miss an important appointment.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-bissa-blue/10 rounded-lg flex items-center justify-center mb-4">
                <MessageSquare className="w-6 h-6 text-bissa-blue" />
              </div>
              <h4 className="text-xl font-semibold text-bissa-blue mb-3">
                Slack & SMS Notifications
              </h4>
              <p className="text-gray-600">
                Get instant notifications via Slack channels or SMS. Stay connected with your team and never miss important updates.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-bissa-blue/10 rounded-lg flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-bissa-blue" />
              </div>
              <h4 className="text-xl font-semibold text-bissa-blue mb-3">
                Marketing Campaigns
              </h4>
              <p className="text-gray-600">
                Create and track email campaigns with open rates, click tracking, and performance analytics.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-bissa-blue/10 rounded-lg flex items-center justify-center mb-4">
                <BarChart3 className="w-6 h-6 text-bissa-blue" />
              </div>
              <h4 className="text-xl font-semibold text-bissa-blue mb-3">
                {t('landing.feature4Title','Analytics & Reports')}
              </h4>
              <p className="text-gray-600">
                {t('landing.feature4Description','Get insights into your business performance with detailed reports and analytics.')}
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-bissa-blue/10 rounded-lg flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-bissa-blue" />
              </div>
              <h4 className="text-xl font-semibold text-bissa-blue mb-3">
                {t('landing.feature6Title','Multi-Language Support')}
              </h4>
              <p className="text-gray-600">
                {t('landing.feature6Description','Use Bissa.ai in your preferred language with support for 10+ African languages.')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div id="pricing" className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h3 className="text-3xl sm:text-4xl font-bold text-bissa-blue mb-4">
              {t('landing.pricingTitle','Simple, Transparent Pricing')}
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('landing.pricingDescription','Choose the plan that fits your business needs. All plans include our core features.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Free Plan */}
            <div className="bg-white rounded-xl p-8 border-2 border-gray-200 hover:border-bissa-blue transition-colors">
              <div className="text-center mb-6">
                <h4 className="text-2xl font-bold text-bissa-blue mb-2">{t('landing.free','Free')}</h4>
                <div className="text-4xl font-bold text-bissa-blue mb-2">$0</div>
                <p className="text-gray-600 text-sm">{t('landing.planFreeDescription','Free Forever')}</p>
              </div>
              <ul className="space-y-2 mb-8 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>1,500 contacts</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>15 users</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>150 tasks/month</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>10 campaigns</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>Core CRM + AI insights</span>
                </li>
              </ul>
              <button
                onClick={() => handleOpenAuthModal('register')}
                className="w-full py-3 bg-bissa-blue text-white rounded-lg hover:bg-bissa-blue-700 transition-colors font-semibold text-sm"
              >
                {t('landing.btnGetStarted','Get Started')}
              </button>
            </div>

            {/* Growth Plan */}
            <div className="bg-white rounded-xl p-8 border-2 border-bissa-blue relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-bissa-blue text-white px-4 py-1 rounded-full text-sm font-semibold">
                  {t('landing.mostPopular','Most Popular')}
                </span>
              </div>
              <div className="text-center mb-6">
                <h4 className="text-2xl font-bold text-bissa-blue mb-2">{t('landing.growth','Growth')}</h4>
                <div className="text-4xl font-bold text-bissa-blue mb-2">$15<span className="text-lg text-gray-600 font-normal">/month</span></div>
                <p className="text-gray-600 text-sm">{t('landing.planGrowthDescription','For growing businesses')}</p>
              </div>
              <ul className="space-y-2 mb-8 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>5,000 contacts</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>3 users</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>500 tasks/month</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>25 campaigns</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>Everything in Free + Advanced Pipeline</span>
                </li>
              </ul>
              <button
                onClick={() => handleOpenAuthModal('register')}
                className="w-full py-3 bg-bissa-gold text-bissa-blue rounded-lg hover:bg-yellow-400 transition-colors font-semibold text-sm"
              >
                {t('landing.btnStartTrial','Start Free Trial')}
              </button>
            </div>

            {/* Pro Plan */}
            <div className="bg-white rounded-xl p-8 border-2 border-gray-200 hover:border-bissa-blue transition-colors">
              <div className="text-center mb-6">
                <h4 className="text-2xl font-bold text-bissa-blue mb-2">{t('landing.pro','Pro')}</h4>
                <div className="text-4xl font-bold text-bissa-blue mb-2">$35<span className="text-lg text-gray-600 font-normal">/month</span></div>
                <p className="text-gray-600 text-sm">{t('landing.planProDescription','For scaling teams')}</p>
              </div>
              <ul className="space-y-2 mb-8 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>25,000 contacts</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>10 users</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>2,500 tasks/month</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>100 campaigns</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>Everything in Growth + Advanced Automation</span>
                </li>
              </ul>
              <button
                onClick={() => handleOpenAuthModal('register')}
                className="w-full py-3 bg-bissa-blue text-white rounded-lg hover:bg-bissa-blue-700 transition-colors font-semibold text-sm"
              >
                {t('landing.btnStartTrial','Start Free Trial')}
              </button>
            </div>

            {/* Business Plan */}
            <div className="bg-white rounded-xl p-8 border-2 border-gray-200 hover:border-bissa-blue transition-colors">
              <div className="text-center mb-6">
                <h4 className="text-2xl font-bold text-bissa-blue mb-2">{t('landing.business','Business')}</h4>
                <div className="text-4xl font-bold text-bissa-blue mb-2">$79<span className="text-lg text-gray-600 font-normal">/month</span></div>
                <p className="text-gray-600 text-sm">{t('landing.planBusinessDescription','For large organizations')}</p>
              </div>
              <ul className="space-y-2 mb-8 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>100,000 contacts</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>Unlimited users</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>10,000 tasks/month</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>500 campaigns</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>Everything in Pro + Dedicated Support</span>
                </li>
              </ul>
              <button
                onClick={() => handleOpenAuthModal('register')}
                className="w-full py-3 bg-bissa-blue text-white rounded-lg hover:bg-bissa-blue-700 transition-colors font-semibold text-sm"
              >
                {t('landing.btnContactSales','Contact Sales')}
              </button>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <p className="text-sm text-gray-600">
              {t('landing.allPlansNote','All plans include 14-day free trial. No credit card required.')}
            </p>
          </div>
        </div>
      </div>

      {/* Bissa Legacy Section */}
      <div className="py-16 sm:py-20 bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="max-w-7xl mx-auto px-6">
          {/* Our Story Badge */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full text-sm font-medium text-orange-700 mb-6">
              {t('landing.storyBadge', 'Our Story')}
            </div>
            
            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-bissa-blue mb-6">
              {t('landing.storyTitle', 'Where Tradition Meets Innovation')}
            </h3>
            
            <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-12">
              {t('landing.storyP1', 'The Bissa people of Burkina Faso have thrived for centuries through strong networks, resilient communities, and trust-based relationships in trade and commerce.')}
            </p>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            {/* Left Column - The Bissa Legacy */}
            <div>
              <h4 className="text-2xl sm:text-3xl font-bold text-bissa-blue mb-6">
                {t('landing.legacyTitle', 'The Bissa Legacy')}
              </h4>
              
              <p className="text-gray-700 mb-6 leading-relaxed">
                {t('landing.legacyP1', 'Known for their adaptability and entrepreneurial spirit, the Bissa people built prosperous trading networks across West Africa through deep relationships, trust, and cultural exchange.')}
              </p>
              
              <p className="text-gray-700 mb-8 leading-relaxed">
                {t('landing.storyP2', 'Today, Bissa.ai carries forward this legacy — empowering modern businesses to build lasting relationships through intelligent connection and AI-driven insights.')}
              </p>
              
              {/* Quote Box */}
              <div className="bg-gray-100 rounded-xl p-6 border-l-4 border-bissa-gold">
                <p className="text-gray-700 italic text-lg leading-relaxed">
                  "{t('landing.legacyQuote', 'Just as the Bissa thrived through strong networks and connections, Bissa.ai empowers businesses to thrive through smarter relationships.')}"
                </p>
              </div>
            </div>

            {/* Right Column - Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/images/bissa-business-connection.jpg"
                  alt="African professionals meeting for business connections"
                  className="w-full h-auto"
                />
                {/* Overlay with text */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-bissa-blue/90 to-transparent text-white p-6">
                  <p className="text-center font-semibold text-lg">
                    {t('landing.legacyTagline', 'Heritage • Innovation • Connection')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Beta Testing CTA Section */}
      <div className="py-16 sm:py-20 bg-gradient-to-br from-bissa-blue to-bissa-blue-700">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-sm font-medium text-white mb-6">
            <Zap className="w-4 h-4" />
            {t('landing.betaBadge', 'Beta Testing Program')}
          </div>
          <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            {t('landing.betaTitle', 'Help Shape the Future of CRM')}
          </h3>
          <p className="text-lg sm:text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {t('landing.betaDescription', 'Bissa.ai is currently in beta testing. Join us and get free access to all premium features during beta!')}
          </p>
          <a
            href="https://bissa.ai/beta"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 sm:px-10 py-4 bg-bissa-gold text-bissa-blue rounded-lg hover:bg-white hover:text-bissa-blue transition-all font-bold text-lg shadow-xl hover:shadow-2xl"
          >
            {t('landing.betaButton', 'Get Beta Access')}
            <ArrowRight className="w-5 h-5" />
          </a>
          <p className="text-sm text-blue-200 mt-6">
            {t('landing.betaNote', 'No credit card required. Free access during beta.')}
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand Section */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-bissa-blue to-bissa-blue-700 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold">B</span>
                </div>
                <h3 className="text-xl font-bold text-white">Bissa.ai</h3>
              </div>
              <p className="text-gray-400 mb-4 max-w-md">
                {t('landing.footerDescription', 'Build relationships like the Bissa people of Burkina Faso — through trust, connection, and AI-driven insights that strengthen every business relationship.')}
              </p>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span>© 2024 Bissa.ai</span>
                <span>•</span>
                <span>{t('landing.footerMade', 'Made with ❤️ for small businesses')}</span>
              </div>
            </div>

            {/* Product Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">{t('landing.footerProduct','Product')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })} className="hover:text-white transition-colors">{t('landing.footerFeatures','Features')}</button></li>
                <li><button onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })} className="hover:text-white transition-colors">{t('landing.footerPricing','Pricing')}</button></li>
                <li><button onClick={() => handleOpenAuthModal('register')} className="hover:text-white transition-colors">{t('landing.footerSignUp','Sign Up')}</button></li>
                <li><button onClick={() => handleOpenAuthModal('login')} className="hover:text-white transition-colors">{t('landing.footerSignIn','Sign In')}</button></li>
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">{t('landing.footerLegal','Legal')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/terms" className="hover:text-white transition-colors">{t('landing.footerTerms','Terms of Service')}</a></li>
                <li><a href="/privacy" className="hover:text-white transition-colors">{t('landing.footerPrivacy','Privacy Policy')}</a></li>
                <li><a href="/cookies" className="hover:text-white transition-colors">{t('landing.footerCookies','Cookie Policy')}</a></li>
                <li><a href="/refunds" className="hover:text-white transition-colors">{t('landing.footerRefunds','Refund Policy')}</a></li>
                <li><a href="/dpa" className="hover:text-white transition-colors">{t('landing.footerDpa','Data Processing')}</a></li>
              </ul>
            </div>
          </div>

          {/* Bottom Border */}
          <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-500">
            <div className="mb-4 md:mb-0">
              <p>{t('landing.footerInspire','Inspired by the Bissa heritage of trust-based commerce and community building.')}</p>
            </div>
            <div className="flex gap-6">
              <a href="/privacy" className="hover:text-white transition-colors">{t('landing.bottomPrivacy','Privacy')}</a>
              <a href="/terms" className="hover:text-white transition-colors">{t('landing.bottomTerms','Terms')}</a>
              <a href="/refunds" className="hover:text-white transition-colors">{t('landing.bottomRefunds','Refunds')}</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        initialMode={authModalMode}
      />
    </div>
  );
};

export default Landing;